import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useIsMobile } from '@/hooks/use-mobile';

export type NavLayout = 'sidebar' | 'topbar' | 'dock' | 'bottom-bar';

const NAV_LAYOUT_KEY = 'nav-layout-v1';
const DEFAULT_LAYOUT: NavLayout = 'sidebar';
const VALID_LAYOUTS: NavLayout[] = ['sidebar', 'topbar', 'dock', 'bottom-bar'];

/** Layouts que nao funcionam bem em mobile */
const DESKTOP_ONLY_LAYOUTS: NavLayout[] = ['sidebar', 'dock'];

function is_valid_layout(v: unknown): v is NavLayout {
  return typeof v === 'string' && VALID_LAYOUTS.includes(v as NavLayout);
}

function load_layout(): NavLayout {
  if (typeof window === 'undefined') return DEFAULT_LAYOUT;
  try {
    const raw = localStorage.getItem(NAV_LAYOUT_KEY);
    if (is_valid_layout(raw)) return raw;
    return DEFAULT_LAYOUT;
  } catch {
    return DEFAULT_LAYOUT;
  }
}

export function useNavLayout() {
  const { user } = useAuth();
  const is_mobile = useIsMobile();
  const [preferred_layout, set_preferred_layout] = useState<NavLayout>(load_layout);

  /** Carrega do Supabase ao montar */
  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;

    const load = async () => {
      const { data } = await supabase
        .from('profiles')
        .select('nav_layout')
        .eq('id', user.id)
        .single();

      if (!cancelled && data) {
        const val = (data as Record<string, unknown>).nav_layout;
        if (is_valid_layout(val)) {
          set_preferred_layout(val);
          try { localStorage.setItem(NAV_LAYOUT_KEY, val); } catch {}
        }
      }
    };

    load();
    return () => { cancelled = true; };
  }, [user?.id]);

  /** Persiste localmente e sincroniza com outros hooks */
  useEffect(() => {
    try { localStorage.setItem(NAV_LAYOUT_KEY, preferred_layout); } catch {}
    window.dispatchEvent(new StorageEvent('storage', { key: NAV_LAYOUT_KEY }));
  }, [preferred_layout]);

  /** Sincroniza entre abas */
  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key === NAV_LAYOUT_KEY || e.key === null) set_preferred_layout(load_layout());
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  const update = useCallback(async (v: NavLayout) => {
    set_preferred_layout(v);
    try { localStorage.setItem(NAV_LAYOUT_KEY, v); } catch {}

    if (user?.id) {
      await supabase
        .from('profiles')
        .update({ nav_layout: v } as Record<string, unknown>)
        .eq('id', user.id);
    }
  }, [user?.id]);

  /** Em mobile, forcar bottom-bar para layouts que nao funcionam */
  const layout: NavLayout = is_mobile && DESKTOP_ONLY_LAYOUTS.includes(preferred_layout)
    ? 'bottom-bar'
    : preferred_layout;

  return { layout, preferred_layout, update };
}
